import { endOfDay, isAfter, isBefore, parseISO, startOfDay } from "date-fns";
import type { ICupomLoja, TipoDesconto } from "interfaces/loja";

export type StatusFilter = "" | "ativo" | "inativo";
export type ValidadeFilter = "" | "valido" | "expirado" | "agendado";

export const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "ativo", label: "Ativos" },
  { value: "inativo", label: "Inativos" },
];

export const VALIDADE_OPTIONS: { value: ValidadeFilter; label: string }[] = [
  { value: "", label: "Qualquer validade" },
  { value: "valido", label: "Válidos agora" },
  { value: "agendado", label: "Agendados" },
  { value: "expirado", label: "Expirados" },
];

export const TIPO_LABELS: Record<TipoDesconto, string> = {
  PORCENTAGEM: "Porcentagem",
  VALOR_FIXO: "Valor fixo",
};

export type ValidadeStatus = "valido" | "expirado" | "agendado" | "inativo";

export function formatValorCupom(cupom: ICupomLoja): string {
  const v = Number(cupom.valor);
  if (cupom.tipo === "PORCENTAGEM") {
    return `${Number.isFinite(v) ? v : cupom.valor}%`;
  }
  return `R$ ${Number.isFinite(v) ? v.toFixed(2) : cupom.valor}`;
}

export function getValidadeStatus(cupom: ICupomLoja): ValidadeStatus {
  if (!cupom.ativo) return "inativo";
  const now = new Date();
  if (cupom.inicioEm) {
    const inicio = startOfDay(parseISO(String(cupom.inicioEm).slice(0, 10)));
    if (isBefore(now, inicio)) return "agendado";
  }
  if (cupom.fimEm) {
    const fim = endOfDay(parseISO(String(cupom.fimEm).slice(0, 10)));
    if (isAfter(now, fim)) return "expirado";
  }
  return "valido";
}

export function validadeChipSx(status: ValidadeStatus) {
  const map: Record<ValidadeStatus, { bg: string; fg: string; label: string }> = {
    valido: { bg: "rgba(16, 185, 129, 0.12)", fg: "#047857", label: "Válido" },
    expirado: { bg: "rgba(239, 68, 68, 0.12)", fg: "#b91c1c", label: "Expirado" },
    agendado: { bg: "rgba(59, 130, 246, 0.12)", fg: "#1d4ed8", label: "Agendado" },
    inativo: { bg: "grey.200", fg: "text.secondary", label: "Inativo" },
  };
  return map[status];
}

export function countVinculos(cupom: ICupomLoja): number {
  const produtos = cupom.cupomProdutos?.length ?? 0;
  const categorias = cupom.cupomCategorias?.length ?? 0;
  const variacoes = cupom.cupomVariacoes?.length ?? 0;
  return produtos + categorias + variacoes;
}

export function isCupomGlobal(cupom: ICupomLoja): boolean {
  return countVinculos(cupom) === 0;
}

export function computeStats(rows: ICupomLoja[]) {
  const ativos = rows.filter((r) => r.ativo).length;
  const validos = rows.filter((r) => getValidadeStatus(r) === "valido").length;
  const usos = rows.reduce((acc, r) => acc + (r.usoTotal ?? 0), 0);
  return { total: rows.length, ativos, validos, usos };
}

export function matchesFilters(
  cupom: ICupomLoja,
  search: string,
  statusFilter: StatusFilter,
  validadeFilter: ValidadeFilter,
): boolean {
  const term = search.trim().toLowerCase();
  if (term && !cupom.codigo.toLowerCase().includes(term)) return false;
  if (statusFilter === "ativo" && !cupom.ativo) return false;
  if (statusFilter === "inativo" && cupom.ativo) return false;
  if (validadeFilter && getValidadeStatus(cupom) !== validadeFilter) return false;
  return true;
}
